/// <amd-module name="Wit/LinkedWorkItemsDropdown/Components/ControlledLinkedWorkItemsDropdown" />
import * as React from "react";
import { Fuzzaldrin, Scorer } from "VSS/Features/FuzzySearch";
import { DropdownExpandableTextField, getHighlightedText } from "VSSUI/Dropdown";
import { CustomEditableDropdown } from "VSSUI/EditableDropdown";
import { IExpandable } from "VSSUI/Expandable";
import { IListBoxItem, ListBoxItemType, LoadingCell } from "VSSUI/ListBox";
import { ITableColumn, SimpleTableCell } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { css } from "VSSUI/Util";
import { IFocusable } from "VSSUI/Utilities/Focus";
import { WorkItem } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import * as Resources from "Wit/LinkedWorkItemsDropdown/Resources";
import { IControlledLinkedWorkItemsDropdownProps } from "./ControlledLinkedWorkItemsDropdown.Props";
import { WorkItemIcon } from "./WorkItemIcon";

export class ControlledLinkedWorkItemsDropdown
    extends React.Component<IControlledLinkedWorkItemsDropdownProps>
    implements IExpandable, IFocusable<{}>
{
    private dropdown = React.createRef<CustomEditableDropdown>();
    private filterMatches: number[][] = [];

    /**
     * Blur events causing the callout to be dismissed are normally handled by the expandable text
     * field. The problem is, we have an action button, and buttons take focus when they are clicked.
     * But in this case, we want the callout to remain open when the action is clicked.
     *
     * The normal(ish) fix for this would be to preventDefault in the mousedown handler of the button
     * to prevent it from taking focus, but something about our FocusWithin component prevents that
     * trick from working. Another solution would have been to inspect the blur events and see if
     * the relatedTarget is the button, but again, the FocusWithin sometimes calls blur callbacks
     * without passing the actual blur event throguh.
     *
     * So what we're left with is this hacky solution where we have a short timeout after clicking
     * the search button during which we will not respond to blur events. Gross! But as far as I
     * can tell, it's the best we can do.
     *
     * The timeout has to be long-ish, because for some reason there are delays of 10-30ms between
     * clicking the button and the blur event finally being fired. 100ms seems safe.
     */
    private preventCollapseOnBlur: boolean = false;

    public render() {
        const { onTextChanged, onValueChanged, onSearchClick, selection, text, workItems } = this.props;

        const searchButtonProps = {
            className: "linked-work-items-dropdown-search-button",
            iconProps: { iconName: "Search" },
            text: Resources.SearchButtonText,
            onClick: this.onSearchClick
        };

        return (
            <CustomEditableDropdown
                actions={onSearchClick ? [searchButtonProps] : undefined}
                items={workItems}
                filterByText={false}
                filterItems={this.filterItems}
                onTextChange={onTextChanged}
                onValueChange={onValueChanged}
                placeholder={Resources.DropdownPlaceholder}
                ref={this.dropdown}
                selection={selection}
                text={text}
                calloutContentClassName="linked-work-items-dropdown-callout"
                renderExpandable={props => <DropdownExpandableTextField {...props} onBlur={this.onBlur} blurDismiss={false} />}
                renderItem={this.renderWorkItem}
            />
        );
    }

    public componentDidMount() {
        if (this.props.focusOnMount) {
            this.focus();
            this.expand();
        }
    }

    public collapse = (): void => {
        if (this.dropdown.current) {
            this.dropdown.current.collapse();
        }
    };

    public expand = (): void => {
        if (this.dropdown.current) {
            this.dropdown.current.expand();
        }
    };

    public focus() {
        if (this.dropdown.current) {
            this.dropdown.current.focus();
        }
    }

    private onSearchClick = () => {
        this.preventCollapseOnBlur = true;
        setTimeout(() => {
            this.preventCollapseOnBlur = false;
        }, 100);
        this.props.onSearchClick?.();
    };

    private onBlur = () => {
        this.props.onBlur?.();
        if (this.preventCollapseOnBlur) {
            // Ignore this blur event - it is caused by clicking the search button.
            // Instead, ensure that focus stays on the text field.
            this.focus();
        } else {
            // Okay, this time we can actually collapse.
            this.collapse();
        }
    };

    private filterItems = (filterTextValue: string) => {
        const { excludedSearchIds, workItems } = this.props;
        if (workItems?.value.length === 1 && workItems.value[0].type === ListBoxItemType.Loading) {
            filterTextValue = ""; // Skip filtering and keep loading placeholder
        }

        let filteredItems: IListBoxItem<WorkItem>[] = [];
        const filterMatchesInternal: number[][] = [];
        const scoredCandidates: { item: IListBoxItem<WorkItem>; index: number; score: number }[] = [];
        const matchedItems: { item: IListBoxItem<WorkItem>; index: number }[] = [];

        const trimmedText = filterTextValue.replace(/\s+/g, "");

        for (let i = 0, l = workItems.length; i < l; i++) {
            const item = workItems.value[i];
            const filterResults = Scorer.isMatch(item.text!, trimmedText.toLowerCase(), trimmedText.toUpperCase());
            const isExcluded = excludedSearchIds && excludedSearchIds.has(item.id);
            if (filterResults && !isExcluded) {
                matchedItems.push({ item, index: i });
            }
        }

        for (const match of matchedItems) {
            if (match.item.text) {
                const score = match.item.id === filterTextValue ? Number.MAX_SAFE_INTEGER : Scorer.score(match.item.text, trimmedText);
                scoredCandidates.push({ item: match.item, index: match.index, score: score });
            }
        }

        scoredCandidates.sort((item1, item2) => {
            return item2.score - item1.score;
        });

        filteredItems = scoredCandidates.map(scoredCandidates => {
            filterMatchesInternal.push(Fuzzaldrin.match(scoredCandidates.item.text, filterTextValue));
            return scoredCandidates.item;
        });

        const filteredIndexMap = scoredCandidates.map(c => c.index);
        this.filterMatches = filterMatchesInternal;

        return { filteredItems, filteredIndexMap, filterMatches: filterMatchesInternal };
    };

    private renderWorkItem = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<IListBoxItem<WorkItem>>,
        tableItem: IListBoxItem<WorkItem>
    ) => {
        if (tableItem.type === ListBoxItemType.Loading) {
            return <LoadingCell columnIndex={columnIndex} key={columnIndex} tableColumn={tableColumn} tableItem={tableItem} />;
        }

        // make sure these are not undefined so we dont get an error calling getHighlightedText
        const filterMatches = this.filterMatches[rowIndex] || [];
        const text = tableItem.text || "";

        return (
            <SimpleTableCell className={css(tableItem.className, tableColumn.className, "scroll-hidden")} columnIndex={columnIndex} key={columnIndex}>
                <WorkItemIcon workItem={tableItem.data!} />
                <Tooltip overflowOnly={true}>{getHighlightedText(text, filterMatches, "text-ellipsis")}</Tooltip>
            </SimpleTableCell>
        );
    };
}
